/**
 * Analytics consent (CNIL / ePrivacy): PostHog may only start after an explicit "accept".
 *
 * The choice lives in localStorage with its date and version. It expires after 13 months,
 * the CNIL's maximum validity for a consent choice, so the banner is shown again then.
 * Bumping CONSENT_VERSION (e.g. a new tracker) invalidates every stored choice.
 */

export type ConsentChoice = 'granted' | 'denied';

interface StoredConsent {
  choice: ConsentChoice;
  date: string;
  version: number;
}

export const CONSENT_STORAGE_KEY = 'vs.consent.analytics';
export const CONSENT_VERSION = 1;
const MAX_AGE_MS = 13 * 30 * 24 * 60 * 60 * 1000;

type Listener = () => void;
const listeners = new Set<Listener>();
// Set by "Gérer les cookies" so the banner reopens even when a valid choice exists.
let reviewRequested = false;
// Fallback when storage is blocked (private mode): the choice holds for this page load only.
let sessionChoice: ConsentChoice | null = null;

function notify(): void {
  listeners.forEach((listener) => listener());
}

function isStoredConsent(value: unknown): value is StoredConsent {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (v.choice === 'granted' || v.choice === 'denied')
    && typeof v.date === 'string'
    && typeof v.version === 'number';
}

/** Returns the stored choice, or null when absent, unreadable, outdated or older than 13 months. */
export function getConsent(now: Date = new Date()): ConsentChoice | null {
  return readStoredConsent(now) ?? sessionChoice;
}

function readStoredConsent(now: Date): ConsentChoice | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isStoredConsent(parsed) || parsed.version !== CONSENT_VERSION) return null;
  const savedAt = Date.parse(parsed.date);
  if (Number.isNaN(savedAt) || now.getTime() - savedAt > MAX_AGE_MS) return null;
  return parsed.choice;
}

export function setConsent(choice: ConsentChoice, now: Date = new Date()): void {
  const stored: StoredConsent = { choice, date: now.toISOString(), version: CONSENT_VERSION };
  sessionChoice = choice;
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // sessionChoice keeps the choice for this page load.
  }
  reviewRequested = false;
  notify();
}

/** Reopens the banner so the user can change a previous choice. */
export function requestConsentReview(): void {
  reviewRequested = true;
  notify();
}

export function isConsentReviewRequested(): boolean {
  return reviewRequested;
}

// A choice made in another tab must hide this tab's banner too.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === CONSENT_STORAGE_KEY || event.key === null) notify();
  });
}

export function subscribeConsent(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
